import type { SpaceExpensesOverview, SpaceLedger } from '@/types/space-settlements';
import type { SpaceBudget, SpaceCommitment, SpaceDetail } from '@/types/spaces';
import { isPending, isUtcInstant } from './commitments';

const DAY = 86_400_000;
/** The home's title: where focus lands when a sheet closes and nothing that opened it can take focus back. */
export const SPACE_HOME_TITLE_ID = 'space-home-title';

/**
 * Where focus goes when a home sheet closes: back to what opened it, or to the home's title when nothing did (a sheet
 * opened by its address finds focus on the page's body, which is always connected) or when the opener is gone.
 */
export function homeSheetFocusTarget(opener: Element | null): HTMLElement | null {
  const { body, documentElement } = opener?.ownerDocument ?? document;
  const opened = opener instanceof HTMLElement && opener !== body && opener !== documentElement && opener.isConnected;
  return opened ? opener : document.getElementById(SPACE_HOME_TITLE_ID);
}

/** How many of the period's latest expenses the home lists. */
export const HOME_RECENT_EXPENSES = 6;
/** How many categories the home lists; the budget sheet has them all. */
export const HOME_CATEGORIES = 5;

// Months as the app's date lines write them: "set", never "set." or "setembro".
const SHORT_MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** A calendar day (`YYYY-MM-DD`, or an instant whose UTC day it is) as "30 set". */
export function dayMonthLabel(day: string): string {
  const [, month, date] = day.slice(0, 10).split('-').map(Number);
  return `${date} ${SHORT_MONTHS[month - 1]}`;
}

const utcDay = (date: Date): string => date.toISOString().slice(0, 10);
const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** A day of a short list (the home's latest expenses) as the personal home heads it: "ter · 22 set". */
export function dayHeading(day: string): string {
  return `${WEEKDAYS[new Date(`${day.slice(0, 10)}T12:00:00Z`).getUTCDay()]} · ${dayMonthLabel(day)}`;
}
const dayValue = (day: string): number => Date.parse(`${day}T00:00:00Z`);

export type TripMoment = { phase: 'before' | 'during'; days: number } | null;

// Trip dates are calendar days (`YYYY-MM-DD`), compared as UTC days so a late-evening clock cannot shift them.
export function tripMoment(space: Pick<SpaceDetail, 'kind' | 'startsAt' | 'endsAt' | 'archivedAt'>, now: Date): TripMoment {
  if (space.kind !== 'trip' || space.archivedAt || !space.startsAt || !space.endsAt) return null;
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const start = Date.parse(`${space.startsAt.slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${space.endsAt.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  if (today < start) return { phase: 'before', days: Math.round((start - today) / DAY) };
  if (today <= end) return { phase: 'during', days: Math.round((end - today) / DAY) };
  return null;
}

export function tripMomentLabel(moment: NonNullable<TripMoment>): string {
  if (moment.phase === 'before') return moment.days === 1 ? 'A viagem começa amanhã' : `A viagem começa em ${moment.days} dias`;
  if (moment.days === 0) return 'Hoje é o último dia da viagem';
  return moment.days === 1 ? 'A viagem termina amanhã' : `A viagem termina em ${moment.days} dias`;
}

// The home's date line, written like the personal home's ("quarta, 23 set · dia 23 de 30"): a month's day for a house,
// the countdown for a trip, and "encerrado" for a closed space, whose month no longer runs.
export function homeDateLine(space: Pick<SpaceDetail, 'kind' | 'startsAt' | 'endsAt' | 'archivedAt'>, now: Date): string {
  const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', timeZone: 'UTC' }).format(now).replace('-feira', '');
  const parts = [`${weekday}, ${dayMonthLabel(utcDay(now))}`];
  const moment = tripMoment(space, now);
  const label = moment ? tripMomentLabel(moment) : null;
  if (label) parts.push(label.charAt(0).toLowerCase() + label.slice(1));
  else if (space.kind !== 'trip' && !space.archivedAt) parts.push(`dia ${now.getUTCDate()} de ${new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate()}`);
  if (space.archivedAt) parts.push('encerrado');
  return parts.join(' · ');
}

/** The hero's title, also the name of its card while it loads or fails: what is available, or what was spent. */
export function homeHeroTitle(space: Pick<SpaceDetail, 'kind'>, limited: boolean, period: string): string {
  if (limited) return space.kind === 'trip' ? 'Disponível na viagem' : 'Disponível no orçamento';
  if (space.kind === 'trip') return 'Gasto na viagem';
  const month = /^\d{4}-\d{2}$/.test(period)
    ? new Intl.DateTimeFormat('pt-BR', { month: 'long', timeZone: 'UTC' }).format(new Date(`${period}-01T12:00:00Z`))
    : null;
  return month ? `Gasto em ${month}` : 'Gasto no período';
}

export interface HomeHeroFigures { limitCents: number | null; spentCents: number; pendingCents: number; availableCents: number | null }

// The hero's "Disponível no orçamento": the period's limit, minus what already entered, minus the recurring expenses
// still to enter. The space's version of the personal "Disponível para gastar" (balance − bill − expected expenses).
// Without a limit there is nothing to be available against, and the hero shows what was spent instead.
export function homeHeroFigures(budget: Pick<SpaceBudget, 'limitCents' | 'spentCents'>, commitments: readonly SpaceCommitment[]): HomeHeroFigures {
  const pendingCents = commitments.filter(isPending).reduce((sum, commitment) => sum + commitment.amountCents, 0);
  const limitCents = budget.limitCents && budget.limitCents > 0 ? budget.limitCents : null;
  return { limitCents, spentCents: budget.spentCents, pendingCents, availableCents: limitCents === null ? null : limitCents - budget.spentCents - pendingCents };
}

// A strip past this many days would stop reading as days; a longer "trip" simply shows none.
const MAX_STRIP_DAYS = 62;

/** The first and last calendar days of a space period: the month of a house, the dates of a trip. */
export function periodBounds(space: Pick<SpaceDetail, 'startsAt' | 'endsAt'>, period: string): { start: string; end: string } | null {
  const month = /^(\d{4})-(\d{2})$/.exec(period);
  if (month) {
    const last = new Date(Date.UTC(Number(month[1]), Number(month[2]), 0)).getUTCDate();
    return { start: `${period}-01`, end: `${period}-${String(last).padStart(2, '0')}` };
  }
  if (period !== 'trip' || !space.startsAt || !space.endsAt) return null;
  const [start, end] = [space.startsAt.slice(0, 10), space.endsAt.slice(0, 10)];
  if (Number.isNaN(dayValue(start)) || Number.isNaN(dayValue(end)) || dayValue(end) < dayValue(start)) return null;
  return { start, end };
}

/** Days from today to the period's last day, both counted; none once it is over. */
export function daysLeft(end: string, today: string): number {
  return Math.max(0, Math.round((dayValue(end) - dayValue(today)) / DAY) + 1);
}

export interface PeriodDay { day: string; phase: 'past' | 'today' | 'future'; due: SpaceCommitment[]; late: boolean }

// The days the hero draws: every day of the period, and on each the recurring expenses still to enter that fall due
// then. A due day that already went by is late (the API's own status, on its UTC day).
export function periodDays(space: Pick<SpaceDetail, 'startsAt' | 'endsAt'>, period: string, today: string, commitments: readonly SpaceCommitment[]): PeriodDay[] {
  const bounds = periodBounds(space, period);
  if (!bounds) return [];
  const count = Math.round((dayValue(bounds.end) - dayValue(bounds.start)) / DAY) + 1;
  if (count > MAX_STRIP_DAYS) return [];
  const due = new Map<string, SpaceCommitment[]>();
  for (const commitment of commitments) {
    if (!isPending(commitment) || !commitment.dueDate) continue;
    const day = commitment.dueDate.slice(0, 10);
    due.set(day, [...(due.get(day) ?? []), commitment]);
  }
  return Array.from({ length: count }, (_, index) => {
    const day = utcDay(new Date(dayValue(bounds.start) + index * DAY));
    const phase = day < today ? 'past' : day === today ? 'today' : 'future';
    const onDay = due.get(day) ?? [];
    return { day, phase, due: onDay, late: onDay.some((commitment) => commitment.status === 'overdue') };
  });
}

// Assigned model: how many of a person's recurring commitments already entered this month, and what they add up to.
export function memberResponsibility(commitments: SpaceCommitment[], userId: string): { entered: number; total: number; amountCents: number; pct: number } {
  const mine = commitments.filter((commitment) => commitment.responsibleUserId === userId);
  const entered = mine.filter((commitment) => commitment.status === 'entered').length;
  return { entered, total: mine.length, amountCents: mine.reduce((sum, commitment) => sum + commitment.amountCents, 0), pct: mine.length ? Math.round(entered * 100 / mine.length) : 0 };
}

export interface BudgetProgress { pct: number; width: number; over: boolean; near: boolean }

// `pct` is what the person reads (it can pass 100); `width` is the bar, clamped.
export function budgetProgress(spentCents: number, limitCents: number | null): BudgetProgress | null {
  if (!limitCents || limitCents <= 0) return null;
  // Multiply before dividing: 12550 / 10000 * 100 is 125.4999… in floating point and would round down.
  const pct = Math.round(spentCents * 100 / limitCents);
  // Thresholds compare the amounts themselves: 90.4% is already above 90 even though it displays as 90%.
  return { pct, width: Math.min(100, Math.max(0, pct)), over: spentCents > limitCents, near: spentCents * 100 > limitCents * 90 && spentCents <= limitCents };
}

export function percentagesLabel(space: Pick<SpaceDetail, 'members'>): string {
  return space.members.map((member) => member.effectivePct.toLocaleString('pt-BR', { maximumFractionDigits: 2 })).join('/');
}

// The home is the space's landing page: a body of the wrong shape (an API that predates a read, a proxy) must
// read as a failed card with a retry, never as a crash of the whole page. `undefined` means still loading.
export function readFailed(read: { error?: unknown; data?: unknown }, valid: (data: unknown) => boolean): boolean {
  return Boolean(read.error) || (read.data !== undefined && !valid(read.data));
}

const isText = (value: unknown): value is string => typeof value === 'string';
const isAmount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// Every field the home formats or walks is checked, rows included: a truncated body would otherwise reach the
// screen as NaN or throw while rendering a row.
export function isOverview(data: unknown): data is SpaceExpensesOverview {
  if (!isRecord(data) || (data.model !== 'split' && data.model !== 'assigned')) return false;
  if (!isAmount(data.sharedAmountCents) || !isRecord(data.expenses) || !isAmount(data.expenses.total) || !Array.isArray(data.expenses.items)) return false;
  return data.expenses.items.every(isExpenseRow);
}

const isOptionalText = (value: unknown): boolean => value === null || value === undefined || isText(value);

// Nullable and nested fields matter as much as the required ones: a departed payer's `payerName` is trimmed,
// and a description rule's `value` is rendered as text, so a non-string in either would throw while rendering.
// Lists group rows by `occurredAt` and the drawer dates `createdAt` (both real UTC instants, as the commitments'
// dates are), the drawer quotes `note`, and every line reads `partial` and walks the splits for the viewer's part.
function isExpenseRow(row: unknown): boolean {
  if (!isRecord(row) || !isText(row.id) || !isText(row.name) || !isText(row.payerUserId) || !isText(row.via) || !isAmount(row.sharedAmountCents)) return false;
  if (!isUtcInstant(row.occurredAt) || !isUtcInstant(row.createdAt) || typeof row.partial !== 'boolean' || !Array.isArray(row.splits) || !row.splits.every(isSplit)) return false;
  if (!isOptionalText(row.payerName) || !isOptionalText(row.categoryId) || !isOptionalText(row.note)) return false;
  return row.source === null || row.source === undefined || isExpenseSource(row.source, row.partial);
}

const isSplit = (split: unknown): boolean => isRecord(split) && isText(split.userId) && isAmount(split.amountCents);

// A partial purchase shows its whole total next to the part that entered, so its payer's details must carry it.
function isExpenseSource(source: unknown, partial: boolean): boolean {
  if (!isRecord(source)) return false;
  const account = source.account === null || source.account === undefined || (isRecord(source.account) && isText(source.account.name));
  const rule = source.rule === null || source.rule === undefined || (isRecord(source.rule) && isText(source.rule.type) && isText(source.rule.value));
  const total = partial ? isAmount(source.totalAmountCents) : source.totalAmountCents === undefined || isAmount(source.totalAmountCents);
  return account && rule && total && isOptionalText(source.transactionId);
}

// The Acerto sheet stores and sends a registration under `periodKey` and `ledgerVersion`, and explains each balance
// with what was sent and received: a body missing any of them would register or explain the wrong thing.
export function isLedger(data: unknown): data is SpaceLedger {
  if (!isRecord(data) || !isText(data.periodKey) || !isText(data.ledgerVersion) || !isAmount(data.sharedAmountCents) || typeof data.canRegister !== 'boolean') return false;
  if (!Array.isArray(data.members) || !Array.isArray(data.suggestedTransfers) || !Array.isArray(data.registeredTransfers)) return false;
  const transfer = (row: unknown) => isRecord(row) && isText(row.fromUserId) && isText(row.toUserId) && isAmount(row.amountCents);
  const member = (row: unknown) => isRecord(row) && isText(row.userId) && isAmount(row.paidCents) && isAmount(row.owedCents)
    && isAmount(row.sentCents) && isAmount(row.receivedCents) && isAmount(row.balanceCents);
  return data.members.every(member) && data.suggestedTransfers.every(transfer) && data.registeredTransfers.every(transfer);
}
